
/**
 * Waits for an element satisfying selector to exist, then resolves promise with the element.
 * Useful for resolving race conditions.
 *
 * @param {String} selector
 * @returns {Promise}
 */
export function elementReady(selector) {
	return new Promise((resolve, reject) => {
		const el = document.querySelector(selector);
		if (el) {
			resolve(el);
			return
		}
		new MutationObserver((mutationRecords, observer) => {
			// Query for elements matching the specified selector
			Array.from(document.querySelectorAll(selector)).forEach((element) => {
				resolve(element);
				//Once we have resolved we don't need the observer anymore.
				observer.disconnect();
			});
		})
		  .observe(document.documentElement, {
			  childList: true,
			  subtree: true
		  });
	});
}

/**
 * watchAwaitSelector
 * * Uses mutation observer to watch for 'selector' involved in DOM changes (added, removed, class change, content change, etc)
 * @param {Function} callback A callback function to be run whenever DOM changes are detected, can be anonymous or named
 * @param {String} selector Element(s) to look for involved in DOM changes, you can watch multiple selectors by separating via comma '.class1, .class2, #id3'
 * @param {Node} rootNode Node to watch from, typically document
 * @param {Number} fallbackDelay Interval to be used if the browser does not support MutationObserver, value is in milliseconds (2000 = 2 seconds)
 */
export const awaitSelector = (selector, rootNode, fallbackDelay) =>
  new Promise((resolve, reject) => {
	  try {
		  const root = rootNode || document;
		  const ObserverClass = MutationObserver || WebKitMutationObserver || null;
		  const mutationObserverSupported = typeof ObserverClass === 'function';
		  let observer;
		  const stopWatching = () => {
			  if (observer) {
				  if (mutationObserverSupported) {
					  observer.disconnect();
				  } else {
					  clearInterval(observer);
				  }
				  observer = null;
			  }
		  };
		  const findAndResolveElements = () => {
			  const allElements = root.querySelectorAll(selector);
			  if (allElements.length === 0) return;
			  const newElements = [];
			  const attributeForBypassing = 'data-awaitselector-resolved';
			  allElements.forEach((el, i) => {
				  if (typeof el[attributeForBypassing] === 'undefined') {
					  allElements[i][attributeForBypassing] = '';
					  newElements.push(allElements[i]);
				  }
			  });
			  if (newElements.length > 0) {
				  stopWatching();
				  resolve(newElements);
			  }
		  };
		  if (mutationObserverSupported) {
			  observer = new ObserverClass((mutationRecords) => {
				  const nodesWereAdded = mutationRecords.reduce(
					(found, record) => found || (record.addedNodes && record.addedNodes.length > 0),
					false
				  );
				  if (nodesWereAdded) {
					  findAndResolveElements();
				  }
			  });
			  observer.observe(root, {
				  childList: true,
				  subtree: true,
				  attributes: true,
				  attributeOldValue: true,
				  characterData: true,
				  characterDataOldValue: true,
			  });
		  } else {
			  observer = setInterval(findAndResolveElements, fallbackDelay || 250);
		  }
		  findAndResolveElements();
	  } catch (exception) {
		  reject(exception);
	  }
  });

export const watchAwaitSelector = (callback, selector, rootNode, fallbackDelay) => {
	(function awaiter(continueWatching = true) {
		if (continueWatching === false) return;
		awaitSelector(selector, rootNode, fallbackDelay).then(callback).then(awaiter);
	})();
};
